import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import pagesAPI from '../../api/pages';
import CreatePageModal from './CreatePageModal';
import './pages.css';

const PagesList = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, published, draft
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchPages();
  }, [filter]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await pagesAPI.getAllPages(params);

      // API returns { status: 200, data: { docs: [...] } }
      if (response?.data?.status === 200 || response?.data?.success) {
        setPages(response.data.data.docs || response.data.data.pages || []);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = () => {
    setShowCreateModal(true);
  };

  const handleEditPage = (pageId) => {
    navigate(`/pages/${pageId}/edit`);
  };

  const handleDuplicatePage = async (pageId, pageName) => {
    if (!window.confirm(`Duplicate "${pageName}"?`)) return;

    try {
      const newPageUrl = `/duplicate-${Date.now()}`;
      const response = await pagesAPI.duplicatePage(pageId, {
        newPageUrl,
        newPageName: `${pageName} (Copy)`,
      });

      if (response?.data?.success) {
        toast.success('Page duplicated successfully');
        fetchPages();
      }
    } catch (error) {
      console.error('Error duplicating page:', error);
      toast.error('Failed to duplicate page');
    }
  };

  const handleDeletePage = async (pageId, pageName) => {
    if (!window.confirm(`Delete "${pageName}"? This action cannot be undone.`)) return;

    try {
      const response = await pagesAPI.deletePage(pageId);

      if (response?.data?.success) {
        toast.success('Page deleted successfully');
        fetchPages();
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handlePublishPage = async (pageId, pageName, currentStatus) => {
    const action = currentStatus === 'published' ? 'unpublish' : 'publish';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} "${pageName}"?`)) return;

    try {
      const response = await pagesAPI.publishPage(pageId);

      if (response?.data?.success) {
        toast.success(`Page ${action}ed successfully`);
        fetchPages();
      }
    } catch (error) {
      console.error(`Error ${action}ing page:`, error);
      toast.error(`Failed to ${action} page`);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      published: <span className="badge bg-success">Published</span>,
      draft: <span className="badge bg-warning text-dark">Draft</span>,
      archived: <span className="badge bg-secondary">Archived</span>,
    };
    return badges[status] || <span className="badge bg-secondary">{status}</span>;
  };

  const getPageTypeBadge = (pageType) => {
    const badges = {
      homepage: <span className="badge bg-primary">Homepage</span>,
      landing: <span className="badge bg-info text-dark">Landing</span>,
      category: <span className="badge bg-success">Category</span>,
      custom: <span className="badge bg-secondary">Custom</span>,
    };
    return badges[pageType] || <span className="badge bg-secondary">{pageType}</span>;
  };

  return (
    <div className="pages-list-container">
      <div className="pages-header">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4>All Pages</h4>
            <p className="text-muted mb-0">Manage your landing pages and content</p>
          </div>
          <button className="btn btn-primary" onClick={handleCreatePage}>
            <i className="fa fa-plus me-2"></i>
            Create New Page
          </button>
        </div>

        <div className="filter-tabs mb-4">
          <button
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
            onClick={() => setFilter('all')}
          >
            All Pages
          </button>
          <button
            className={`btn ${filter === 'published' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
            onClick={() => setFilter('published')}
          >
            Published
          </button>
          <button
            className={`btn ${filter === 'draft' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('draft')}
          >
            Drafts
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading pages...</p>
        </div>
      ) : pages.length === 0 ? (
        <div className="empty-state text-center py-5">
          <i className="fa fa-file-alt fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">No pages found</h5>
          <p className="text-muted">Create your first page to get started</p>
          <button className="btn btn-primary mt-3" onClick={handleCreatePage}>
            <i className="fa fa-plus me-2"></i>
            Create Page
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr className=''>
                <th>Page Name</th>
                <th>URL</th>
                <th>Type</th>
                <th>Sections</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page._id}>
                  <td>
                    <strong>{page.pageName || page.title || 'Untitled'}</strong>
                  </td>
                  <td>
                    <code>{page.pageUrl || page.route || '/'}</code>
                  </td>
                  <td>{getPageTypeBadge(page.pageType)}</td>
                  <td>
                    <span className="badge bg-light text-dark">
                      {page.sections?.length || page.sectionCount || page.blocks?.length || 0} sections
                    </span>
                  </td>
                  <td>{getStatusBadge(page.status)}</td>
                  <td>{new Date(page.updatedAt).toLocaleDateString()}</td>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => handleEditPage(page._id)}
                        title="Edit"
                      >
                        <i className="fa fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-outline-success"
                        onClick={() => handlePublishPage(page._id, page.pageName || page.title, page.status)}
                        title={page.status === 'published' ? 'Unpublish' : 'Publish'}
                      >
                        <i className={`fas fa-${page.status === 'published' ? 'eye-slash' : 'eye'}`}></i>
                      </button>
                      <button
                        className="btn btn-outline-info"
                        onClick={() => handleDuplicatePage(page._id, page.pageName || page.title)}
                        title="Duplicate"
                      >
                        <i className="fa fa-copy"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => handleDeletePage(page._id, page.pageName || page.title)}
                        title="Delete"
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Page Modal */}
      <CreatePageModal
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          fetchPages(); // Refresh list after modal closes
        }}
      />
    </div>
  );
};

export default PagesList;
